// Copyright Caproc Oy
// System name: Aqua-Net
// System module: Sensor
//
// 3.4.2026 TS Google Places Autocomplete for address fields

import React from "react";
import { Input, Button } from "semantic-ui-react";

const GOOGLE_MAPS_SCRIPT_URL = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&libraries=places`;

const loadGoogleMapsScript = (callback) => {
    if (window.google && window.google.maps && window.google.maps.places) {
        callback();
        return;
    }
    const existing = document.querySelector(`script[src^="https://maps.googleapis.com/maps/api/js"]`);
    if (existing) {
        existing.addEventListener('load', callback);
        return;
    }
    const script = document.createElement('script');
    script.src = GOOGLE_MAPS_SCRIPT_URL;
    script.onload = callback;
    document.head.appendChild(script);
};

const extractAddressComponent = (components, type) => {
    const component = components.find(c => c.types.includes(type));
    return component ? component.long_name : '';
};

const extractAddressComponentShort = (components, type) => {
    const component = components.find(c => c.types.includes(type));
    return component ? component.short_name : '';
};

class AddressAutocomplete extends React.Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.autocomplete = null;
    }

    componentDidMount() {
        loadGoogleMapsScript(() => {
            this.initAutocomplete();
        });
    }

    componentWillUnmount() {
        if (this.autocomplete) {
            window.google.maps.event.clearInstanceListeners(this.autocomplete);
        }
    }

    initAutocomplete = () => {
        if (!this.inputRef.current || !window.google) return;
        const inputElement = this.inputRef.current.querySelector('input') || this.inputRef.current;
        this.autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
            types: ['address'],
            componentRestrictions: { country: ['fi', 'se', 'no', 'dk', 'ee'] },
            fields: ['address_components', 'geometry', 'formatted_address']
        });
        this.autocomplete.addListener('place_changed', this.handlePlaceChanged);
    };

    handlePlaceChanged = () => {
        const place = this.autocomplete.getPlace();
        if (!place || !place.address_components) return;

        const components = place.address_components;
        const streetNumber = extractAddressComponent(components, 'street_number');
        const route = extractAddressComponent(components, 'route');
        const address = route ? `${route} ${streetNumber}`.trim() : '';
        const postalCode = extractAddressComponent(components, 'postal_code');
        const city = extractAddressComponent(components, 'locality')
            || extractAddressComponent(components, 'administrative_area_level_3')
            || extractAddressComponent(components, 'postal_town');
        const country = extractAddressComponentShort(components, 'country').toLowerCase();
        const lat = place.geometry ? place.geometry.location.lat() : 0;
        const lng = place.geometry ? place.geometry.location.lng() : 0;

        if (this.props.onPlaceSelected) {
            this.props.onPlaceSelected({
                address,
                postal_code: postalCode,
                city,
                country: country || 'fi',
                latitude: lat,
                longitude: lng
            });
        }
    };

    handleGeocode = () => {
        if (!window.google || !window.google.maps) return;
        const geocoder = new window.google.maps.Geocoder();
        const { address, postalCode, city } = this.props;
        const fullAddress = `${address || ''}, ${postalCode || ''} ${city || ''}`.trim();
        if (!fullAddress || fullAddress === ',') return;

        geocoder.geocode({ address: fullAddress }, (results, status) => {
            if (status === 'OK' && results[0]) {
                if (this.props.onGeocode) {
                    this.props.onGeocode({
                        latitude: results[0].geometry.location.lat(),
                        longitude: results[0].geometry.location.lng()
                    });
                }
            }
        });
    };

    render() {
        const { value, name, onChange, t, showGeocodeButton } = this.props;
        return (
            <div ref={this.inputRef}>
                <Input
                    value={value}
                    name={name}
                    onChange={onChange}
                    fluid
                />
                {showGeocodeButton && (
                    <Button
                        type="button"
                        size="mini"
                        basic
                        style={{ marginTop: '4px' }}
                        onClick={this.handleGeocode}
                    >
                        {t ? t("Fetch coordinates") : "Fetch coordinates"}
                    </Button>
                )}
            </div>
        );
    }
}

export default AddressAutocomplete;
